import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ShoppingCart, BookOpen, Store, BookMarked } from "lucide-react";
import { BOOK, getSaleState } from "@/lib/site";
import Starfield from "./Starfield";
import WaitlistForm from "./WaitlistForm";

const line = {
  hidden: { y: 44, opacity: 0 },
  show: { y: 0, opacity: 1 },
};

const titleLines = ["Hannah's", "Heart", "Awakens"];

export default function Hero() {
  const saleState = getSaleState();
  const isPromo = saleState === "launchweek";
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 12 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 12 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
      <Starfield count={70} />
      {/* ambient heart glow */}
      <div
        aria-hidden
        className="heart-glow pointer-events-none absolute -top-10 right-[8%] hidden h-[520px] w-[520px] rounded-full lg:block"
        style={{ background: "radial-gradient(circle, rgba(251,191,36,0.32) 0%, rgba(251,191,36,0.08) 40%, transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
        {/* Left */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-body text-xs uppercase tracking-[0.25em] text-gold"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {isPromo
              ? "Available Now · Kindle Launch Sale — Just $0.99 Through July 31, 2026"
              : "Available Now · Ebook, Paperback, and Hardcover"}
          </motion.p>

          <motion.h1
            variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } }}
            initial="hidden"
            animate="show"
            className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-cream md:text-6xl lg:text-7xl"
          >
            {titleLines.map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  variants={line}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${i === 2 ? "italic text-gold" : ""}`}
                >
                  {t}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-md font-body text-lg leading-relaxed text-slate-300"
          >
            {BOOK.subtitle}. A tender picture book following a curious little girl who
            discovers a love that changes everything.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9"
          >
            <div className="flex flex-col gap-3 sm:flex-row" data-testid="hero-buy-buttons">
              <a
                href={BOOK.amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-amazon"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 font-medium text-twilight transition-[background-color,transform,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(251,191,36,0.55)] active:scale-95"
              >
                <ShoppingCart className="h-4 w-4" />
                Buy on Amazon
              </a>
              <a
                href={BOOK.barnesNoble}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-bn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color,box-shadow] duration-300 hover:bg-gold hover:text-twilight hover:shadow-[0_0_20px_rgba(251,191,36,0.45)]"
              >
                <BookOpen className="h-4 w-4" />
                Barnes &amp; Noble
              </a>
              <a
                href={BOOK.walmart}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-walmart"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color,box-shadow] duration-300 hover:bg-gold hover:text-twilight hover:shadow-[0_0_20px_rgba(251,191,36,0.45)]"
              >
                <Store className="h-4 w-4" />
                Walmart
              </a>
              <a
                href={BOOK.kharis}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-buy-kharis"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color,box-shadow] duration-300 hover:bg-gold hover:text-twilight hover:shadow-[0_0_20px_rgba(251,191,36,0.45)]"
              >
                <BookMarked className="h-4 w-4" />
                Kharis Publishing
              </a>
            </div>
            <div className="mt-6">
              <p className="mb-2 font-body text-sm text-slate-400">
                Or join the mailing list for launch updates & bonuses:
              </p>
              <WaitlistForm variant="hero" />
            </div>
          </motion.div>
        </div>

        {/* Right - book cover with 3D tilt */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center lg:justify-end"
          style={{ perspective: 1200 }}
        >
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <motion.img
              src="/book/cover.jpg"
              alt={`${BOOK.title} book cover`}
              data-testid="hero-book-cover"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="w-[300px] rounded-xl border border-white/10 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.7)] md:w-[380px]"
              style={{ transform: "translateZ(40px)" }}
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="relative z-10 mx-auto mt-16 flex max-w-7xl justify-center px-6 md:px-12"
      >
        <a href="#story" className="font-body text-xs uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-gold">
          Scroll to begin the story ↓
        </a>
      </motion.div>
    </section>
  );
}
